import axios from "axios";
import store from "store";
import { ServiceTypeEnum } from "../../utils/enums";
import general from "../../utils/general";
import CommandBus from '../../infrastructure/command-bus/command-bus';
import * as CommandTypeEnum from '../../infrastructure/command-bus/command-type-enum';

// Token refresh için flag - aynı anda birden fazla refresh isteği gönderilmesini önler
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const makeApiCall = async payload => {
    try {
        if (payload.query) {
            payload.query = general.removeNullValues(payload.query);
        }
        const sendData = general.isNullOrEmpty(payload?.data) ? {} : { ...payload.data };
        
        // Use proxy in development, full URL in production
        const baseUrl =  "https://danismanim-api.algoraft.com/api/v1";
        let url = baseUrl + "/" + payload.url;

        if (payload?.query instanceof Object) {
            const keys = Object.keys(payload?.query);

            if (keys?.length > 0) {
                if (url.endsWith('&') || url.endsWith('?')) {
                    url = url.slice(0, -1);
                }
                if (!url.includes('?')) {
                    url += "?";
                }
            }

            let query = "";
            for (let i = 0; i < keys.length; ++i) {
                const object = payload?.objectKeys?.find(item => item === keys[i]) != null;

                query += keys[i] + "=" + encodeURI(
                    object
                        ? JSON.stringify(payload?.query[keys[i]])
                        : (payload?.query[keys[i]] instanceof Array
                            ? payload?.query[keys[i]].join(",")
                            : payload?.query[keys[i]])
                );

                if (i < keys.length - 1) {
                    query += "&";
                }
            }

            if (query) {
                url += (url.endsWith('?') ? '' : '&') + query;
            }

            if (payload?.saveFilter) {
                const base_url = window.location.href.split("?")[0];
                window.history.pushState({}, null, base_url + "?" + query);
            }
        }
        
        // Get token
        let token = store.get("token");
        if (token && typeof token === 'object' && token.accessToken) {
            token = token.accessToken;
        }
        const newToken = general.isNullOrEmpty(payload.token) ? token : payload.token;
        
        // Build headers
        const headers = {
            "Content-Type": "application/json"
        };
        
        if (newToken && !general.isNullOrEmpty(newToken)) {
            headers["Authorization"] = "Bearer " + newToken;
        }
        
        const { headers: axiosHeaders, ...axiosOptionsWithoutHeaders } = payload?.axiosOptions || {};
        
        const finalHeaders = { ...headers };
        if (axiosHeaders) {
            Object.assign(finalHeaders, axiosHeaders);
        }
        
        if (!newToken || general.isNullOrEmpty(newToken)) {
            delete finalHeaders.Authorization;
        } else if (finalHeaders.Authorization && finalHeaders.Authorization.includes('undefined')) {
            delete finalHeaders.Authorization;
        }
        
        const method = payload.method?.toUpperCase() || 'GET';
        
        // GET ve DELETE isteklerinde body gönderme
        const isBodyAllowed = ['POST', 'PUT', 'PATCH'].includes(method);
        
        if (!isBodyAllowed) {
            delete finalHeaders["Content-Type"];
        }
        
        // Check if FormData is being sent - if so, remove Content-Type to let axios set it automatically
        const isFormData = axiosOptionsWithoutHeaders?.data instanceof FormData || sendData instanceof FormData;
        if (isFormData) {
            delete finalHeaders["Content-Type"];
        }
        
        let options = {
            url,
            headers: finalHeaders,
            method: method,
            timeout: 30000,
            withCredentials: false,
            ...axiosOptionsWithoutHeaders
        };
        
        if (isBodyAllowed && !axiosOptionsWithoutHeaders?.data) {
            options.data = sendData;
        }
        
        const res = await axios(options);
        
        if (res.status === 200 || res.status === 201 || res.status === 204) {
            if (res.data?.success === false) {
                return {
                    error: true,
                    data: res.data?.data,
                    errorMessage: res.data?.message || res.data?.key || "error.unknown_error",
                }
            }
            return {
                success: true,
                data: res.data,
                pagination: res.data?.pagination
            }
        }
        return {
            error: true,
            data: res.data?.data,
            errorMessage: res.data?.message || res.data?.key || "error.unknown_error",
            errorMessageTechnical: "Unknown response status. status: " + res.status,
        }
    } catch (error) {
        // Network error or CORS error
        if (!error?.response) {
            const errorMessage = error?.message || "Network error";
            if (errorMessage.includes("CORS") || errorMessage.includes("Network Error") || error?.code === "ERR_NETWORK") {
                return {
                    error: true,
                    data: null,
                    errorMessage: "CORS hatası: Sunucu isteği engelliyor.",
                    errorMessageTechnical: errorMessage,
                    useI18next: false
                }
            }
            return {
                error: true,
                data: null,
                errorMessage: "Sunucu ile bağlantı kurulamadı",
                errorMessageTechnical: errorMessage,
                useI18next: false
            }
        }
        
        const statusCodeString = error?.response?.status + "";
        
        if (error?.response?.status === 401) {
            // Auth endpoint'lerinde 401 = yanlış credentials
            const isAuthEndpoint = payload.url?.includes('auth/login') || 
                                   payload.url?.includes('auth/register') ||
                                   payload.url?.includes('auth/forgot-password');
            
            if (isAuthEndpoint) {
                return {
                    error: true,
                    data: error?.response?.data?.data,
                    errorMessage: error?.response?.data?.error || error?.response?.data?.message || error?.response?.data?.message_key || "E-posta veya şifre hatalı",
                    useI18next: false
                }
            }
            
            // Diğer endpoint'ler için refresh token
            const refreshToken = store.get("refreshToken");
            
            if (!refreshToken || general.isNullOrEmpty(refreshToken)) {
                general.notificationError("error.unauthorized");
                setTimeout(() => {
                    store.remove("token");
                    store.remove("refreshToken");
                    store.remove("tokenExpiresAt");
                    window.location.href = "/auth/login"
                }, 2000);
                return {
                    error: true,
                    errorMessage: "error.unauthorized"
                }
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then(token => {
                    return makeApiCall({ ...payload, token: token });
                }).catch(err => {
                    return {
                        error: true,
                        errorMessage: "Token refresh failed",
                        errorMessageTechnical: err?.message || "Unknown error"
                    };
                });
            }

            isRefreshing = true;

            const baseUrl = "https://danismanim-api.algoraft.com/api/v1";
            const refreshUrl = baseUrl + "/auth/refresh";

            try {
                const refreshResponse = await axios({
                    url: refreshUrl,
                    method: 'POST',
                    headers: {
                        "Content-Type": "application/json"
                    },
                    data: {
                        refreshToken: refreshToken
                    }
                });
                
                if (refreshResponse?.status === 401) {
                    throw new Error("Refresh token expired");
                }
                
                const tokens = refreshResponse?.data?.data?.tokens || refreshResponse?.data?.tokens || refreshResponse?.data;
                if (tokens && tokens.accessToken) {
                    store.set("token", tokens.accessToken);
                    if (tokens.refreshToken) {
                        store.set("refreshToken", tokens.refreshToken);
                    }
                    if (tokens.expiresAt) {
                        store.set("tokenExpiresAt", tokens.expiresAt);
                    }
                    
                    const newToken = tokens.accessToken;
                    isRefreshing = false;
                    
                    processQueue(null, newToken);
                    
                    return makeApiCall({ ...payload, token: newToken });
                } else {
                    throw new Error("Invalid token format in refresh response");
                }
            } catch (refreshError) {
                isRefreshing = false;
                processQueue(refreshError, null);
                
                general.notificationError("error.unauthorized");
                setTimeout(() => {
                    store.remove("token");
                    store.remove("refreshToken");
                    store.remove("tokenExpiresAt");
                    window.location.href = "/auth/login"
                }, 2000);
                return {
                    error: true,
                    errorMessage: "Token refresh failed",
                    errorMessageTechnical: refreshError?.message || "Unknown error"
                };
            }
        }
        
        if (statusCodeString.startsWith("4")) {
            return {
                error: true,
                errorMessage: error?.response?.data?.message || error?.response?.data?.responseData || error?.response?.data?.key,
            }
        }
        
        if (error?.response?.status === 500) {
            return {
                error: true,
                errorMessage: error?.response?.data?.errorCode,
            }
        }
        else {
            if (payload.serviceType === ServiceTypeEnum.User || payload.serviceType === ServiceTypeEnum.Auth) {
                CommandBus.sc.publish(CommandTypeEnum.MaintenenceMode, { status: true, text: null })
                return null;
            }
            else {
                return {
                    error: true,
                    data: null,
                    errorMessage: "Sunucu ile bağlantı kurulamadı",
                    errorMessageTechnical: error?.message,
                    useI18next: false
                }
            }
        }
    }
};

const apiService = {
    makeApiCall
};

export default apiService;
